import { ReactNode, useState, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LoadingPage } from '@/components/LoadingPage';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [showInitialLoading, setShowInitialLoading] = useState(true);
  const hasShownLoading = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Show loading for minimum 1.5 seconds when user first becomes authenticated
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    // When user is present and auth is not loading anymore
    if (user && !authLoading) {
      // If we've already shown loading, hide it immediately
      if (hasShownLoading.current) {
        setShowInitialLoading(false);
        return;
      }
      
      hasShownLoading.current = true;
      
      // Keep showing loading for minimum 1.5 seconds
      timerRef.current = setTimeout(() => {
        setShowInitialLoading(false);
      }, 1500);
    }
    
    // If user is not logged in, don't show loading
    if (!user && !authLoading) {
      setShowInitialLoading(false);
      hasShownLoading.current = false;
    }
  }, [user, authLoading]);

  // If not logged in, redirect to login immediately without showing loading
  if (!user && !authLoading) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading when user is logged in but we're still in the initial loading period
  if (showInitialLoading && user) {
    return (
      <LoadingPage 
        message="Preparing your dashboard..." 
        variant="full" 
      />
    );
  }

  return <>{children}</>;
}
